import { useState, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  CalendarIcon, 
  UserGroupIcon, 
  CurrencyDollarIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

// Define types
interface UserInfo {
  email: string;
  role: string;
}

interface TenantInfo {
  id: string;
  name: string;
  subdomain: string;
}

// Serializable types - matching the Server Component interface
interface StatCard {
  title: string;
  value: string | number;
  change?: {
    value: number;
    trend: 'up' | 'down';
  };
  iconName: 'calendar' | 'users' | 'currency' | 'document'; // String instead of component
  color: 'blue' | 'green' | 'amber' | 'red' | 'purple';
}

interface RecentActivity {
  id: string;
  type: 'appointment' | 'patient' | 'payment' | 'record';
  title: string;
  description: string;
  time: string;
  status: 'completed' | 'pending' | 'cancelled';
}

interface DashboardClientProps {
  initialStats: StatCard[];
  initialActivities: RecentActivity[];
}

function DashboardClient({ initialStats, initialActivities }: DashboardClientProps) {
  const { t } = useTranslation(['dashboard', 'common']);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(null);
  const stats = initialStats;
  const recentActivities = initialActivities;

  useEffect(() => {
    // Simulate user context (in real app, this would come from auth)
    setUserInfo({
      email: 'doctor@practice.com',
      role: 'Dentist',
    });

    setTenantInfo({
      id: '1',
      name: 'Dental Practice Pro',
      subdomain: 'practice',
    });
  }, []);

  // Get icon component from string name
  const getStatIcon = (iconName: StatCard['iconName']) => {
    const iconMap = {
      calendar: CalendarIcon,
      users: UserGroupIcon,
      currency: CurrencyDollarIcon,
      document: DocumentTextIcon,
    };
    return iconMap[iconName];
  };

  const getStatCardColorClasses = (color: StatCard['color']) => {
    const colorMap = {
      blue: {
        bg: 'bg-icon-bg-primary',
        text: 'text-stats-card-title',
        icon: 'text-icon-primary'
      },
      green: {
        bg: 'bg-icon-bg-success',
        text: 'text-stats-card-title',
        icon: 'text-icon-success'
      },
      amber: {
        bg: 'bg-icon-bg-warning',
        text: 'text-stats-card-title',
        icon: 'text-icon-warning'
      },
      red: {
        bg: 'bg-icon-bg-danger',
        text: 'text-stats-card-title',
        icon: 'text-icon-danger'
      },
      purple: {
        bg: 'bg-icon-bg-secondary',
        text: 'text-stats-card-title',
        icon: 'text-icon-secondary'
      }
    };
    return colorMap[color];
  };

  const getActivityIcon = (type: RecentActivity['type']) => {
    const iconMap = {
      appointment: CalendarIcon,
      patient: UserGroupIcon,
      payment: CurrencyDollarIcon,
      record: DocumentTextIcon
    };
    return iconMap[type];
  };

  const getStatusIcon = (status: RecentActivity['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-4 w-4 text-icon-success" />;
      case 'pending':
        return <ClockIcon className="h-4 w-4 text-icon-warning" />;
      case 'cancelled':
        return <ExclamationTriangleIcon className="h-4 w-4 text-icon-danger" />;
      default:
        return <ClockIcon className="h-4 w-4 text-icon-muted" />;
    }
  };

  return (
    <div className="space-y-8">
      {/* Enhanced Header */}
      <div className="relative overflow-hidden rounded-2xl p-8 border-2 border-primary/20 shadow-lg" style={{
        background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.08), rgba(99, 102, 241, 0.08))',
        backdropFilter: 'blur(20px)'
      }}>
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">
                {(() => {
                  const hour = new Date().getHours();
                  const { i18n } = useTranslation();
                  const currentLang = i18n.language;
                  
                  if (currentLang === 'fr') {
                    // French: Bonjour (morning/afternoon) or Bonsoir (evening)
                    return hour < 18 ? t('greeting.morning') : t('greeting.evening');
                  } else if (currentLang === 'ar') {
                    // Arabic: Use appropriate greeting based on time
                    return hour < 12 ? t('greeting.morning') : hour < 18 ? t('greeting.afternoon') : t('greeting.evening');
                  } else {
                    // English: Good morning/afternoon/evening
                    return `${t('greeting.good')} ${hour < 12 ? t('greeting.morning') : hour < 18 ? t('greeting.afternoon') : t('greeting.evening')}`;
                  }
                })()}!
              </h1>
              <p className="text-lg text-muted-foreground">
                {t('greeting.welcome')}
              </p>
              {userInfo && (
                <div className="mt-4 flex items-center space-x-4">
                  <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold shadow-lg border border-white/20" style={{
                    background: 'linear-gradient(135deg, rgb(14, 165, 233), rgb(59, 130, 246))',
                    color: 'white'
                  }}>
                    {userInfo.role}
                  </span>
                  {tenantInfo && (
                    <span className="text-sm text-muted-foreground">
                      {tenantInfo.name}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="hidden lg:block">
              <div className="h-24 w-24 rounded-2xl flex items-center justify-center shadow-xl border border-white/20" style={{
                background: 'linear-gradient(135deg, rgb(14, 165, 233), rgb(99, 102, 241))',
                boxShadow: '0 10px 25px -5px rgba(14, 165, 233, 0.3), 0 10px 10px -5px rgba(14, 165, 233, 0.1)'
              }}>
                <ChartBarIcon className="h-12 w-12 text-white drop-shadow-sm" />
              </div>
            </div>
          </div>
        </div>
        {/* Decorative background */}
        <div className="absolute top-0 right-0 -mt-4 -mr-4 h-32 w-32 rounded-full blur-2xl" style={{
          background: 'radial-gradient(circle, rgba(14, 165, 233, 0.15), transparent)'
        }}></div>
        <div className="absolute bottom-0 left-0 -mb-4 -ml-4 h-24 w-24 rounded-full blur-xl" style={{
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.12), transparent)'
        }}></div>
      </div>

      {/* Enhanced Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const colors = getStatCardColorClasses(stat.color);
          const Icon = getStatIcon(stat.iconName);
          
          return (
            <div 
              key={index} 
              className="bg-card border border-border rounded-lg p-6 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground group-hover:text-card-foreground transition-colors">
                    {stat.title}
                  </p>
                  <p className="text-3xl font-bold text-card-foreground mt-2">
                    {stat.value}
                  </p>
                  {stat.change && (
                    <div className="flex items-center mt-2">
                      {stat.change.trend === 'up' ? (
                        <ArrowTrendingUpIcon className="h-4 w-4 text-icon-success mr-1" />
                      ) : (
                        <ArrowTrendingDownIcon className="h-4 w-4 text-icon-danger mr-1" />
                      )}
                      <span className={`text-sm font-medium ${
                        stat.change.trend === 'up' ? 'text-icon-success' : 'text-icon-danger'
                      }`}>
                        {stat.change.value}%
                      </span>
                      <span className="text-sm text-muted-foreground ml-1">
                        {t('stats.vsLastPeriod')}
                      </span>
                    </div>
                  )}
                </div>
                <div className={`p-3 rounded-xl ${colors.bg} group-hover:scale-110 transition-transform`}>
                  <Icon className={`h-6 w-6 ${colors.icon}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-card-foreground">{t('recentActivity.title')}</h3>
              <button className="text-sm font-medium text-primary hover:text-primary/80 transition-colors">
                {t('recentActivity.viewAll')}
              </button>
            </div>
            <div className="space-y-4">
              {recentActivities.map((activity) => {
                const ActivityIcon = getActivityIcon(activity.type);
                
                return (
                  <div 
                    key={activity.id} 
                    className="flex items-start space-x-4 p-4 rounded-xl activity-item hover:bg-activity-item-hover active:bg-activity-item-pressed transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    tabIndex={0}
                    role="button"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        // Handle activity item click
                        console.log('Activity clicked:', activity.title);
                      }
                    }}
                  >
                    <div className="flex-shrink-0 p-2 bg-activity-icon activity-icon rounded-lg transition-all duration-200">
                      <ActivityIcon className="h-5 w-5 text-icon-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-activity-item truncate">
                          {activity.title}
                        </p>
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(activity.status)}
                          <span className="text-xs text-activity-item-muted">
                            {activity.time}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-activity-item-muted mt-1">
                        {activity.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Quick Actions & Summary */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-card-foreground mb-4">{t('quickActions.title')}</h3>
            <div className="space-y-3">
              <button className="w-full flex items-center justify-between p-3 text-left bg-btn-primary-bg text-btn-primary-text rounded-xl hover:bg-btn-primary-hover active:bg-btn-primary-pressed transition-all duration-200 group btn-enhanced focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2">
                <span className="font-medium">{t('quickActions.newAppointment')}</span>
                <CalendarIcon className="h-5 w-5 group-hover:scale-110 transition-transform text-btn-primary-text" />
              </button>
              <button className="w-full flex items-center justify-between p-3 text-left bg-btn-success-bg text-btn-success-text rounded-xl hover:bg-btn-success-hover active:bg-btn-success-pressed transition-all duration-200 group btn-enhanced focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2">
                <span className="font-medium">{t('quickActions.addPatient')}</span>
                <UserGroupIcon className="h-5 w-5 group-hover:scale-110 transition-transform text-btn-success-text" />
              </button>
              <button className="w-full flex items-center justify-between p-3 text-left bg-btn-secondary-bg text-btn-secondary-text rounded-xl hover:bg-btn-secondary-hover active:bg-btn-secondary-pressed transition-all duration-200 group btn-enhanced focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2">
                <span className="font-medium">{t('quickActions.createInvoice')}</span>
                <DocumentTextIcon className="h-5 w-5 group-hover:scale-110 transition-transform text-btn-secondary-text" />
              </button>
            </div>
          </div>

          {/* Practice Summary */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-card-foreground mb-4">{t('practiceSummary.title')}</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">{t('practiceSummary.todayProgress')}</span>
                  <span className="font-medium text-card-foreground">75%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="h-2 rounded-full" style={{ 
                    width: '75%',
                    backgroundColor: 'rgb(var(--icon-primary))'
                  }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">{t('practiceSummary.weeklyTarget')}</span>
                  <span className="font-medium text-card-foreground">85%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="h-2 rounded-full" style={{ 
                    width: '85%',
                    backgroundColor: 'rgb(var(--icon-success))'
                  }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">{t('practiceSummary.monthlyRevenue')}</span>
                  <span className="font-medium text-card-foreground">92%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="h-2 rounded-full" style={{ 
                    width: '92%',
                    backgroundColor: 'rgb(var(--icon-warning))'
                  }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Export memoized component for better performance
export default memo(DashboardClient);
